import React, { useCallback, useEffect, useImperativeHandle } from "react";
import { Form as AntForm, Col, Row } from "antd";
import Localization from "../../localization";
import AutoComplete from "../autoComplete";
import styled from "styled-components";
import { useForm } from "../../hooks";
import Checkbox from "../checkbox";
import Select from "../select";
import Button from "../button";
import Input from "../input";

const Item = ({ face, ...props }) => {
  if (face === "select") return <Select {...props} />;
  else if (face === "checkbox") return <Checkbox {...props} />;
  else if (face === "autoComplete") return <AutoComplete {...props} />;
  return <Input {...props} />;
};

const Submit = ({ ...props }) => <Button id="form_submit_button" {...props} />;

const Styled = styled(AntForm)`
  display: flex;
  gap: ${(_) => _.gap};
  flex-direction: column;
  .ant-form-item-explain-error {
    font-weight: 400;
    margin-top: 0.5rem;
    line-height: 1.9rem;
    color: ${(_) => _.theme.token.fontColors.destructive};
  }
`;

const Form = React.forwardRef(
  (
    {
      children,
      fields = [],
      gap = "2rem",
      renderFormItem = () => null,
      ...props
    },
    ref
  ) => {
    const formHook = useForm({});
    const form = props.form || formHook;
    const flatFields = fields.flat();
    const filedValues = flatFields.filter((_) => _.value !== undefined);

    useEffect(() => {
      form.resetFields();
    }, []);

    useEffect(() => {
      if (!!filedValues.length)
        form.setFields(
          filedValues.map((_) => ({ name: _.name, value: _.value }))
        );
    }, [filedValues]);

    useImperativeHandle(ref, () => form, []);

    const renderItem = useCallback((item) => {
      const { label, name, rules, valuePropName, face = "", ...props } = item;
      return (
        renderFormItem(item) ?? (
          <AntForm.Item
            name={name}
            className={`w-100 m-0`}
            valuePropName={valuePropName}
            onFocus={() => form.onFieldFocus(name)}
            children={
              <Item face={face} label={label} rules={rules} {...props} />
            }
            rules={rules?.map((rule) =>
              rule.type === "phone"
                ? {
                    required: true,
                    pattern: /^[\s]*[+0][\d\s-]{8,}[\s]*$/,
                    message: Localization.feedback.invalid_phone,
                  }
                : rule
            )}
          />
        )
      );
    }, []);

    const handleKeyDown = (e) => {
      if (
        e.key === "Enter" &&
        form.isFieldFocused(flatFields[flatFields.length - 1].name)
      ) {
        const button = document.getElementById("form_submit_button");
        if (!!button) button.click();
      }
    };

    return (
      <Styled
        gap={gap}
        form={form}
        layout="vertical"
        autoComplete="on"
        onKeyDown={handleKeyDown}
        {...props}
      >
        {React.Children.toArray(
          fields.map((item) =>
            Array.isArray(item) ? (
              <Row
                gutter={[12, 12]}
                children={item.map((data) => (
                  <Col
                    sm={24}
                    xs={24}
                    md={24 / item.length}
                    children={renderItem(data)}
                  />
                ))}
              />
            ) : (
              renderItem(item)
            )
          )
        )}
        {children}
      </Styled>
    );
  }
);

Form.useWatch = AntForm.useWatch;
Form.Submit = Submit;

export default Form;
